import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useId, useRef, useState } from 'react';
import type { FinancialOverview } from './financial-overview-api';
import { RemunerationLegend, remunerationMoney } from './RemunerationColumn';
import { PrototypeIcon } from './PrototypeIcon';

/** Un seul parcours : la roue ouvre ensemble le réglage et l'onboarding de XD.
 * Le dialogue natif conserve le focus, Échap et le retour au contrôle d'origine. */
export function RemunerationGoalDialog({
  overview,
  draft,
  onDraft,
  onCancel,
  onSave,
  saving = false,
  error = null,
}: {
  readonly overview: FinancialOverview;
  readonly draft: string;
  readonly onDraft: (value: string) => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
  readonly saving?: boolean;
  readonly error?: string | null;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useId();
  const [anchors, setAnchors] = useState<readonly { top: number; left: number }[]>([]);
  useEffect(() => {
    dialog.current?.showModal();
    // Les repères XD suivent les lignes réelles, même si un montant fait varier
    // leur hauteur. Ces coordonnées de présentation ne sont pas des règles métier.
    const rows = document.querySelectorAll('.remuneration-card--current dl > div');
    const placeAnchors = (): void => {
      setAnchors(
        [1, 2, 3, 4, 6].flatMap((index) => {
          const row = rows[index];
          if (!row) return [];
          const bounds = row.getBoundingClientRect();
          return [{ top: bounds.top + bounds.height / 2 - 12, left: bounds.right + 24 }];
        }),
      );
    };
    placeAnchors();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(placeAnchors);
    rows.forEach((row) => observer?.observe(row));
    window.addEventListener('resize', placeAnchors);
    window.addEventListener('scroll', placeAnchors);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', placeAnchors);
      window.removeEventListener('scroll', placeAnchors);
    };
  }, []);
  return (
    <ModalDialog
      ref={dialog}
      className="remuneration-goal-overlay"
      aria-labelledby={heading}
      onCancel={onCancel}
      close={
        <ModalCloseButton
          className="remuneration-goal-overlay__close"
          aria-label="Fermer"
          onClick={onCancel}
        />
      }
    >
      <div className="remuneration-onboarding__anchors" aria-hidden="true">
        {anchors.map((anchor, index) => (
          <span key={index} style={{ top: anchor.top, left: anchor.left }}>
            {index + 1}
          </span>
        ))}
      </div>

      <aside
        className="remuneration-onboarding"
        aria-label="Comprendre les chiffres de rémunération"
      >
        <ol>
          <li>
            Repère basé sur trois mois facturés
            <br />
            et le dernier PDF URSSAF.
            <br />
            Le montant versable demande les encaissements
            <br />
            et charges qualifiés.
          </li>
          <li>
            Si nous avons détecté dans votre comptabilité
            <br />
            la rémunération prise sur le mois en cours.
            <br />
            La santé de la trésorerie est alors calculée
            <br />
            sur la rémunération réellement prise
          </li>
          <li>
            Nous calculons automatiquement le fond de roulement
            <br />
            Que votre prise de rémunération a généré.
            <br />
            En rouge si négatif. En vert si positif.
          </li>
          <li>
            Ici, la couleur est un indicateur de l’impact sur la trésorerie
            <br />
            De l’entreprise de cette prise de rémunération le mois dernier
          </li>
          <li>
            Ici nous additionnons les rémunérations prises sur l’année en cours,
            <br />
            Mois après mois et calculons l’impact global sur la trésorerie.
          </li>
        </ol>
        <p>
          La couleur des chiffres reflète le niveau de trésorerie
          <br />
          qui découle de ce calcul.
        </p>
        <RemunerationLegend />
      </aside>
      <form
        className="remuneration-goal"
        onSubmit={(event) => {
          event.preventDefault();
          onSave();
        }}
      >
        <header className="tosho-modal-heading tosho-modal-header">
          <h2 id={heading}>
            <PrototypeIcon name="coins" />
            Rémunération
          </h2>
        </header>
        <label>
          Mon objectif
          <span>
            <input
              type="number"
              min="1"
              max="10000000"
              step=".01"
              value={draft}
              onChange={(event) => onDraft(event.currentTarget.value)}
              autoFocus
            />
            €
          </span>
        </label>
        <div
          className="remuneration-goal__advice"
          title={`Estimation prudente, réserve conservée et provision URSSAF : ${remunerationMoney(overview.remuneration?.provisionDueCents)} (${overview.remuneration?.provisionPeriod ?? 'PDF absent'}). Le conseil inclut ce qui est déjà pris ; les autres échéances ne sont pas anticipées.`}
        >
          <span>Conseillé ce mois-ci</span>
          <strong>{remunerationMoney(overview.remuneration?.advisedCents)}</strong>
        </div>
        <p>
          Suivre le conseil de Tosho permet de garder
          <br />
          une trésorerie saine et de constituer un fond
          <br />
          de roulement tranquillement.
        </p>
        <p>
          La jauge en bas à gauche
          <br />
          permet de connaître en temps réel l’impact
          <br />
          sur la trésorerie de cette rémunération.
        </p>
        {error ? (
          <p className="tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="remuneration-goal__actions">
          <button type="button" disabled={saving} onClick={onCancel}>
            Annuler
          </button>
          <button type="submit" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Valider'}
          </button>
        </div>
      </form>
    </ModalDialog>
  );
}
